import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, RefreshCw, Archive, Pencil, Eye, Loader2, ListChecks, ShieldCheck } from "lucide-react";
import { VersionHistory } from "@/components/VersionHistory";
import { ChecklistRuns } from "@/components/ChecklistRuns";
import { toast } from "sonner";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useSession } from "@/context/Session";
import { StatusBadge, ConfidencePill } from "@/components/Badges";
import { SourcesPanel } from "@/components/SourcesPanel";
import { ClarificationsPanel } from "@/components/ClarificationsPanel";
import { TranslatedDoc } from "@/components/TranslatedDoc";
import { AudiencePanel, TagChips } from "@/components/AudiencePanel";
import { t } from "@/i18n";
import { Tt } from "@/components/Tt";

function FlagBanners({ sop }) {
  const open = (sop.flags || []).filter((f) => f.status === "open");
  return (
    <>
      {sop.status === "outdated" && (
        <div className="banner banner-warn" data-testid="sop-outdated-banner">
          <AlertTriangle size={18} className="text-[var(--status-warning)] shrink-0" />
          <div>{t("This procedure is outdated and excluded from answers.")}{" "}
            {sop.superseded_by && <Link className="underline" to={`/sops/${sop.superseded_by}`}>{t("Open the current version")}</Link>}</div>
        </div>
      )}
      {sop.stale && <div className="banner banner-warn" data-testid="sop-stale-banner"><AlertTriangle size={18} className="text-[var(--status-warning)]" /> {t("Not updated in over 6 months — please re-confirm.")}</div>}
      {open.map((f) => (
        <div key={f.id} className="banner banner-warn" data-testid={`sop-flag-${f.id}`}>
          <AlertTriangle size={18} className="text-[var(--status-warning)] shrink-0" />
          <div><b className="capitalize">{t(f.type === "contradiction" ? "Possible contradiction" : "Outdated")}</b>
            {f.other_sop_id && <> {t("with")} <Link className="underline" to={`/sops/${f.other_sop_id === sop.id ? f.sop_id : f.other_sop_id}`}><Tt text={f.other_sop_id === sop.id ? f.sop_title : f.other_sop_title} /></Link></>}: {f.detail}</div>
        </div>
      ))}
    </>
  );
}

function Actions({ sop, api, reload, editing, setEditing }) {
  const openQs = (sop.clarifications || []).filter((c) => !c.answer).length;
  const [note, setNote] = useState("");
  const act = async (path, msg, body) => {
    if (path === "publish" && openQs && !window.confirm(t("Unanswered clarification questions: {n}. Publish anyway?", { n: openQs }))) return;
    try { await api.post(`/sops/${sop.id}/${path}`, body); toast.success(t(msg)); setNote(""); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const canPublish = ["draft", "outdated"].includes(sop.status) && sop.markdown;
  return (
    <div className="flex flex-wrap gap-2">
      {canPublish && (
        <div className="flex gap-2 w-full sm:w-auto">
          <input className="field !py-2 text-sm sm:w-64" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("What changed? (approval note)")} data-testid="sop-publish-note-input" />
          <button className="btn btn-primary shrink-0" onClick={() => act("publish", "Published — now the source of truth", { note })} data-testid="sop-publish-button"><CheckCircle2 size={16} /> {t("Publish")}</button>
        </div>
      )}
      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(!editing)} disabled={sop.status === "processing"} data-testid="sop-edit-toggle-button">{editing ? <><Eye size={14} /> {t("Preview")}</> : <><Pencil size={14} /> {t("Edit")}</>}</button>
      <button className="btn btn-ghost btn-sm" onClick={() => act("regenerate", "Regenerating from sources")} disabled={sop.status === "processing"} data-testid="sop-regenerate-button"><RefreshCw size={14} /> {t("Regenerate")}</button>
      <button className="btn btn-ghost btn-sm" onClick={() => act("archive", "Archived")} data-testid="sop-archive-button"><Archive size={14} /> {t("Archive")}</button>
    </div>
  );
}

function Editor({ sop, api, done }) {
  const [title, setTitle] = useState(sop.title);
  const [md, setMd] = useState(sop.markdown);
  const save = async () => {
    try { await api.patch(`/sops/${sop.id}`, { title, markdown: md }); toast.success(t("Saved as draft — publish to make it authoritative")); done(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="stack-sm">
      <input className="field font-bold" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sop-title-input" />
      <textarea className="field font-mono text-sm min-h-[480px]" value={md} onChange={(e) => setMd(e.target.value)} data-testid="sop-markdown-textarea" />
      <button className="btn btn-primary" onClick={save} data-testid="sop-save-button">{t("Save changes")}</button>
    </div>
  );
}

export default function SopDetail() {
  const { id } = useParams();
  const api = useApi();
  const { me } = useSession();
  const [sop, setSop] = useState(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("doc");
  const [err, setErr] = useState("");

  const load = useCallback(() => api.get(`/sops/${id}`).then((r) => setSop(r.data)).catch((e) => setErr(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const leakCheck = sop?.tags?.includes("customer") && sop?.published_version && !sop.customer_ok && !sop.customer_hold;
    if (!sop || (sop.status !== "processing" && !sop.checking && !leakCheck)) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [sop, load]);

  if (err) return <p className="page" data-testid="sop-error">{err}</p>;
  if (!sop) return <p className="page faint">{t("Loading…")}</p>;
  const processing = sop.status === "processing";

  return (
    <div className="page grid lg:grid-cols-12 gap-s">
      <div className="lg:col-span-8 stack">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={sop.status} /><ConfidencePill level={sop.confidence} testid="sop-confidence-pill" />
          <span className="chip chip-muted" data-testid="sop-updated-date">{t("Updated")} {fmtDate(sop.updated_at)}</span>
          <span className="chip chip-muted">v{sop.version}</span>
          {me.is_maintainer && <TagChips tags={sop.tags} testid="sop-tags" />}
          {sop.checking && <span className="chip chip-info"><Loader2 size={12} className="animate-spin" /> {t("Checking for contradictions")}</span>}
        </div>
        {sop.rejected && <div className="banner banner-err" data-testid="sop-rejected-banner"><AlertTriangle size={18} className="text-[var(--status-error)] shrink-0" /><div><b>{t("Not approved")}</b>{sop.reject_reason ? `: ${sop.reject_reason}` : ` — ${t("by the owner.")}`}</div></div>}
        <FlagBanners sop={sop} />
        {sop.status === "failed" && <div className="banner banner-err" data-testid="sop-failed-banner"><AlertTriangle size={18} /> {t("Drafting failed")}: {sop.error}. {t("Try Regenerate.")}</div>}
        {me.is_maintainer && !processing && <Actions sop={sop} api={api} reload={load} editing={editing} setEditing={setEditing} />}
        {me.is_maintainer && sop.published_version && sop.status !== "published" && !processing && (
          <div className="banner banner-info" data-testid="sop-draft-over-published-banner"><ShieldCheck size={18} className="text-[var(--status-info)] shrink-0" />{t("Staff still see published v{v} until you publish this draft.", { v: sop.published_version })}</div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          {me.is_maintainer && [["doc", "Document"], ["versions", "Version history"]].map(([v, l]) => (
            <button key={v} className={`nav-link ${tab === v ? "active" : ""}`} onClick={() => setTab(v)} data-testid={`sop-tab-${v}`}>{t(l)}</button>
          ))}
          {(sop.status === "published" || sop.published_version) && sop.status !== "outdated" && (
            <Link to={`/sops/${sop.id}/checklist`} className="btn btn-ghost btn-sm ml-auto" data-testid="sop-checklist-button"><ListChecks size={14} /> {t("Start shift checklist")}</Link>
          )}
        </div>
        <div className="card">
          {processing ? (
            <div className="py-16 text-center" data-testid="sop-processing-state">
              <Loader2 className="mx-auto animate-spin text-[var(--accent-primary)]" size={30} />
              <p className="font-semibold mt-4">{t("Extracting sources and drafting SOP…")}</p>
              <p className="text-sm faint mt-1">{t("Transcription of video/audio can take a minute.")}</p>
            </div>
          ) : tab === "versions" ? <VersionHistory sopId={sop.id} /> : editing ? <Editor sop={sop} api={api} done={() => { setEditing(false); load(); }} /> : <TranslatedDoc sop={sop} />}
        </div>
      </div>
      <aside className="lg:col-span-4 stack">
        {me.is_maintainer && <AudiencePanel sop={sop} api={api} reload={load} />}
        {me.is_maintainer && <ClarificationsPanel sop={sop} api={api} reload={load} />}
        {me.is_maintainer && sop.published_version && <ChecklistRuns sopId={sop.id} />}
        <SourcesPanel sources={sop.sources} />
      </aside>
    </div>
  );
}
